import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CAMBIOS, COMBUSTIVEIS, CORES, ESTADOS, STATIC_BRANDS, OPCIONAIS, CONDICOES } from "@/lib/carOptions";
import { X, Loader2, ImagePlus, Upload, ShieldCheck, Check, Info } from "lucide-react";

const EMPTY = {
  marca: "", modelo: "", versao: "", ano: "", ano_fabricacao: "", preco: "", km: "",
  cor: "", cambio: "", combustivel: "", cidade: "", estado: "", blindado: false,
  placa: "", features: [], condicoes: [], descricao: "",
};

export function AnuncioForm({ initial, images: initialImages, onSubmit, saving, submitLabel, onCancel }) {
  const [brands, setBrands] = useState(STATIC_BRANDS);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(initialImages || []);
  const [f, setF] = useState({ ...EMPTY });

  useEffect(() => { api.get("/brands").then(({ data }) => { if (data?.length) setBrands(data); }).catch(() => {}); }, []);
  useEffect(() => {
    if (initial) {
      setF({
        ...EMPTY, ...initial,
        ano: String(initial.ano ?? ""), ano_fabricacao: String(initial.ano_fabricacao ?? ""),
        preco: String(initial.preco ?? ""), km: String(initial.km ?? ""),
        features: initial.features || [], condicoes: initial.condicoes || [],
      });
    }
    if (initialImages) setImages(initialImages);
  }, [initial, initialImages]);

  const set = (k) => (v) => { if (v === "") return; setF((s) => ({ ...s, [k]: v })); };
  const setText = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (key, opt) => setF((s) => ({ ...s, [key]: s[key].includes(opt) ? s[key].filter((x) => x !== opt) : [...s[key], opt] }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const toAdd = files.slice(0, 8 - images.length);
    if (!toAdd.length) { toast.info("Máximo de 8 fotos."); return; }
    setUploading(true);
    try {
      for (const file of toAdd) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.path]);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!f.marca || !f.modelo || !f.ano || !f.preco || !f.km) {
      toast.error("Preencha marca, modelo, ano, preço e KM."); return;
    }
    onSubmit({
      ...f, ano: parseInt(f.ano),
      ano_fabricacao: f.ano_fabricacao ? parseInt(f.ano_fabricacao) : parseInt(f.ano),
      preco: parseFloat(f.preco), km: parseInt(f.km), images,
    });
  };

  const Chip = ({ list, active, onClick, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-sm font-600 px-3 py-2 rounded-sm border transition-colors ${active ? "border-blaze bg-blaze/5 text-obsidian" : "border-border bg-white hover:border-obsidian"}`}>
      {active ? <Check className="w-3.5 h-3.5 text-blaze" strokeWidth={3} /> : <span className="text-muted-foreground">+</span>}{list}
    </button>
  );

  return (
    <form onSubmit={submit} className="space-y-8">
      <section className="bg-white border border-border rounded-sm p-6">
        <Label className="font-700">Fotos do carro <span className="text-muted-foreground font-500">({images.length}/8)</span></Label>
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
          {images.map((img, i) => (
            <div key={i} className="relative aspect-square rounded-sm overflow-hidden border border-border group">
              <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
              {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] font-700 bg-obsidian text-white px-1.5 py-0.5 rounded-sm">Capa</span>}
              <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))}
                className="absolute top-1 right-1 w-6 h-6 rounded-sm bg-obsidian text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-img-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {images.length < 8 && (
            <label className="aspect-square rounded-sm border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-blaze transition-colors" data-testid="upload-label">
              {uploading ? <Loader2 className="w-6 h-6 animate-spin text-blaze" /> : <ImagePlus className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />}
              <span className="text-[11px] text-muted-foreground mt-1">Adicionar</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="upload-input" />
            </label>
          )}
        </div>
      </section>

      <section className="bg-white border border-border rounded-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <Label>Marca *</Label>
          <Select value={f.marca} onValueChange={set("marca")}>
            <SelectTrigger className="mt-2 rounded-sm" data-testid="input-marca"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Modelo *</Label><Input value={f.modelo} onChange={(e) => setText("modelo")(e.target.value)} placeholder="Ex: Civic EXL" className="mt-2 rounded-sm" data-testid="input-modelo" /></div>
        <div className="md:col-span-2"><Label>Versão</Label><Input value={f.versao} onChange={(e) => setText("versao")(e.target.value)} placeholder="Ex: 2.0 Turbo AWD" className="mt-2 rounded-sm" data-testid="input-versao" /></div>
        <div><Label>Ano do modelo *</Label><Input value={f.ano} onChange={(e) => setText("ano")(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="2021" className="mt-2 rounded-sm" data-testid="input-ano" /></div>
        <div><Label>Ano de fabricação</Label><Input value={f.ano_fabricacao} onChange={(e) => setText("ano_fabricacao")(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="2020" className="mt-2 rounded-sm" data-testid="input-ano-fab" /></div>
        <div><Label>Preço (R$) *</Label><Input value={f.preco} onChange={(e) => setText("preco")(e.target.value.replace(/\D/g, ""))} placeholder="128900" className="mt-2 rounded-sm" data-testid="input-preco" /></div>
        <div><Label>Quilometragem *</Label><Input value={f.km} onChange={(e) => setText("km")(e.target.value.replace(/\D/g, ""))} placeholder="32000" className="mt-2 rounded-sm" data-testid="input-km" /></div>
        <div>
          <Label>Cor</Label>
          <Select value={f.cor} onValueChange={set("cor")}>
            <SelectTrigger className="mt-2 rounded-sm" data-testid="input-cor"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{CORES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Câmbio</Label>
          <Select value={f.cambio} onValueChange={set("cambio")}>
            <SelectTrigger className="mt-2 rounded-sm" data-testid="input-cambio"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{CAMBIOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Combustível</Label>
          <Select value={f.combustivel} onValueChange={set("combustivel")}>
            <SelectTrigger className="mt-2 rounded-sm" data-testid="input-combustivel"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>{COMBUSTIVEIS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-3 border border-border rounded-sm px-4 mt-8 h-10 cursor-pointer hover:border-obsidian transition-colors" data-testid="input-blindado-wrap">
          <Checkbox checked={f.blindado} onCheckedChange={(v) => setF((s) => ({ ...s, blindado: !!v }))} data-testid="input-blindado" />
          <span className="text-sm font-600 flex items-center gap-1"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Blindado</span>
        </label>
        <div><Label>Cidade</Label><Input value={f.cidade} onChange={(e) => setText("cidade")(e.target.value)} placeholder="São Paulo" className="mt-2 rounded-sm" data-testid="input-cidade" /></div>
        <div>
          <Label>Estado</Label>
          <Select value={f.estado} onValueChange={set("estado")}>
            <SelectTrigger className="mt-2 rounded-sm" data-testid="input-estado"><SelectValue placeholder="UF" /></SelectTrigger>
            <SelectContent>{ESTADOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </section>

      <section className="bg-white border border-border rounded-sm p-6 space-y-6">
        <div>
          <Label className="font-700">Opcionais</Label>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {OPCIONAIS.map((opt) => <Chip key={opt} list={opt} active={f.features.includes(opt)} onClick={() => toggle("features", opt)} testid={`chip-${opt}`} />)}
          </div>
        </div>
        <div>
          <Label className="font-700">Condições</Label>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {CONDICOES.map((opt) => <Chip key={opt} list={opt} active={f.condicoes.includes(opt)} onClick={() => toggle("condicoes", opt)} testid={`cond-${opt}`} />)}
          </div>
        </div>
      </section>

      <section className="bg-white border border-border rounded-sm p-6 grid grid-cols-1 gap-5">
        <div><Label>Descrição</Label><Textarea value={f.descricao} maxLength={500} onChange={(e) => setText("descricao")(e.target.value)} placeholder="Detalhes do veículo, estado de conservação..." className="mt-2 rounded-sm min-h-28" data-testid="input-descricao" /></div>
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2.5">
          <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-xs text-amber-800"><b>Não inclua telefone ou dados pessoais</b>. O contato é feito pelo chat interno da PicCar.</p>
        </div>
        <div>
          <Label>Placa <span className="text-muted-foreground font-500">(informação interna, não exibida)</span></Label>
          <Input value={f.placa} onChange={(e) => setText("placa")(e.target.value.toUpperCase().slice(0, 8))} placeholder="ABC1D23" className="mt-2 rounded-sm uppercase max-w-xs" data-testid="input-placa" />
        </div>
      </section>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving} className="h-12 px-8 bg-obsidian hover:bg-blaze text-white font-700 rounded-sm" data-testid="submit-anuncio-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} {submitLabel}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} data-testid="cancel-btn">Cancelar</Button>
      </div>
    </form>
  );
}
